import React, { useEffect, useRef, useState } from "react";
import { Play, MoreVertical, Download, Trash2, X, Maximize2 } from "lucide-react";

import { TruncatedTitle } from "../common/Primitives";


export function VideoCard({ task, onClick, inAllTab = false }) {
  const isPending = task.status === 'pending' || task.status === 'processing';
  const isCompleted = task.status === 'completed';
  const isFailed = task.status === 'failed';

  return (
    <div
      className={`library-card library-card-video ${isCompleted ? 'library-card-clickable' : ''}`}
      onClick={isCompleted ? onClick : undefined}
      data-testid={`video-task-${task.id}`}
    >
      <div className="library-card-cover library-card-video-thumb">
        {task.thumbnail_url ? (
          <img src={task.thumbnail_url} alt={task.song_title} />
        ) : (
          <div className="library-card-icon-video">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          </div>
        )}
        {isCompleted && (
          <div className="video-play-overlay">
            <Play size={24} fill="currentColor" />
          </div>
        )}
      </div>

      <div className="library-card-content">
        <TruncatedTitle title={task.song_title || 'Video'} />
        <div className="library-card-subtitle subtle">
          {inAllTab && <span className="type-label type-video">Video</span>}
          {isCompleted ? (
            <span>Ready to view</span>
          ) : isFailed ? (
            <span className="text-red">Failed</span>
          ) : (
            <span className="text-accent">Processing...</span>
          )}
        </div>
      </div>

      {isPending && (
        <div className="library-card-status">
          <span className="spinner" />
        </div>
      )}
    </div>
  );
}


export function VideoDetailView({ video, onClose, onDelete, onDownload }) {
  const videoRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleDownload = () => {
    if (video.video_url) {
      const link = document.createElement('a');
      link.href = video.video_url;
      link.download = `${video.song_title || 'video'}.mp4`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this video? This cannot be undone.')) {
      onDelete && onDelete(video);
    }
    setMenuOpen(false);
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen().catch(err => {
          console.log('Fullscreen error:', err);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div className="panel panel-detail panel-video-detail" ref={containerRef}>
      <div className="panel-title-row">
        <h2 className="panel-title">{video.song_title || 'Video'}</h2>
        <div className="panel-title-actions">
          <div className="menu-container" ref={menuRef}>
            <button
              className="btn-icon"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="video-detail-menu-btn"
            >
              <MoreVertical size={20} />
            </button>
            {menuOpen && (
              <div className="dropdown-menu dropdown-menu-right">
                <button
                  className="dropdown-item"
                  onClick={handleDownload}
                  data-testid="video-download-btn"
                >
                  <Download size={16} />
                  Download
                </button>
                <button
                  className="dropdown-item dropdown-item-danger"
                  onClick={handleDelete}
                  data-testid="video-delete-menu-btn"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            )}
          </div>
          <button className="btn-icon" onClick={onClose} data-testid="close-video-detail-btn">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="video-player-container">
        {video.video_url ? (
          <>
            <video
              ref={videoRef}
              src={video.video_url}
              controls
              className="video-player"
              poster={video.thumbnail_url}
              data-testid="video-player"
            />
            <button
              className="video-fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              data-testid="video-fullscreen-btn"
            >
              <Maximize2 size={18} />
            </button>
          </>
        ) : (
          <div className="video-player-placeholder">
            <Play size={48} />
            <span>Video not available</span>
          </div>
        )}
      </div>

      {video.thumbnail_url && (
        <div className="video-detail-info">
          <div className="video-detail-thumb">
            <img src={video.thumbnail_url} alt="Thumbnail" />
          </div>
          <div className="video-detail-meta">
            <div className="video-detail-status">
              <span className="type-label type-video">Video</span>
              <span className="text-accent">Ready to view</span>
            </div>
            {video.created_at && (
              <div className="video-detail-date subtle">
                Created {new Date(video.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export function VideoDebugPanel({ task, onClose, onRefresh }) {
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh || task.status === 'completed' || task.status === 'failed') return;

    const interval = setInterval(() => {
      onRefresh && onRefresh();
    }, 2000);

    return () => clearInterval(interval);
  }, [autoRefresh, task.status, onRefresh]);

  const getStatusColor = (status) => {
    if (!status) return '#666';
    if (status === 'completed') return '#10b981';
    if (status === 'failed' || status === 'skipped_or_failed') return '#ef4444';
    if (status === 'processing' || status === 'submitting') return '#f59e0b';
    return '#3b82f6';
  };

  return (
    <div className="video-debug-panel" data-testid="video-debug-panel">
      <div className="debug-header">
        <h3>🔧 Video Generation Debug</h3>
        <div className="debug-header-actions">
          <label className="debug-auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button className="btn-icon" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="debug-content">
        <div className="debug-section">
          <div className="debug-section-title">Task Info</div>
          <div className="debug-row">
            <span className="debug-label">Task ID:</span>
            <span className="debug-value mono">{task.id}</span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Status:</span>
            <span className="debug-value" style={{ color: getStatusColor(task.status) }}>
              {task.status?.toUpperCase() || 'unknown'}
            </span>
          </div>
          <div className="debug-row">
            <span className="debug-label">Title:</span>
            <span className="debug-value">{task.song_title || 'N/A'}</span>
          </div>
        </div>

        <div className="debug-section">
          <div className="debug-section-title">Processing Status</div>
          <div className="debug-row">
            <span className="debug-label">1. Nano Banana:</span>
            <span className="debug-value" style={{ color: getStatusColor(task.nano_banana_status) }}>
              {task.nano_banana_status || 'pending'}
            </span>
          </div>
          <div className="debug-row">
            <span className="debug-label">2. Kling:</span>
            <span className="debug-value" style={{ color: getStatusColor(task.kling_status) }}>
              {task.kling_status || 'pending'}
            </span>
          </div>
          {task.kling_task_id && (
            <div className="debug-row">
              <span className="debug-label">Kling Task ID:</span>
              <span className="debug-value mono">{task.kling_task_id}</span>
            </div>
          )}
        </div>

        <div className="debug-section">
          <div className="debug-section-title">Thumbnail (from Nano Banana)</div>
          {task.thumbnail_url ? (
            <div className="debug-thumbnail">
              <img src={task.thumbnail_url} alt="Thumbnail" />
              <div className="debug-url mono">{task.thumbnail_url}</div>
            </div>
          ) : (
            <div className="debug-value" style={{ color: '#f59e0b' }}>
              No thumbnail received yet
            </div>
          )}
        </div>

        {task.video_url && (
          <div className="debug-section">
            <div className="debug-section-title">Video URL (from Kling)</div>
            <div className="debug-url mono">{task.video_url}</div>
          </div>
        )}

        {task.error_message && (
          <div className="debug-section debug-error">
            <div className="debug-section-title">Error</div>
            <div className="debug-error-text">{task.error_message}</div>
          </div>
        )}
      </div>
    </div>
  );
}
